import React, { useEffect, useMemo } from 'react'
import { useStateValue } from '@State'
import { saveAnswer } from '../../api'
import { FeedbackDisplay } from './utils/FeedbackDisplay'
import { TabSplitLayout } from './utils/TabSplitLayout'
import { SaveIndicator } from './utils/SaveIndicator'
import {
  FEEDBACK_COLORS, FEEDBACK_LABELS, getOptionFeedbackStatus,
  OPTION_FEEDBACK_STYLE, FEEDBACK_BADGE,
  FEEDBACK_KEY_STYLE, FEEDBACK_KEY_ITEM_STYLE, FEEDBACK_KEY_SWATCH,
} from './utils/feedbackStyles'

const OPTION_BASE = {
  display: 'flex', alignItems: 'center',
  border: '2px solid #dee2e6', borderRadius: '10px',
  padding: '0.6rem 0.85rem', marginBottom: '0.5rem',
  backgroundColor: '#f8f9fa',
}

const LABEL_BASE = { display: 'flex', alignItems: 'center', margin: 0, flex: 1, cursor: 'pointer', gap: '0.5rem' }
const LABEL_DISABLED = { ...LABEL_BASE, cursor: 'default' }
const INPUT_STYLE = { width: '18px', height: '18px', flexShrink: 0, cursor: 'pointer' }

const MAX_REACHED_STYLE = {
  color: '#856404', backgroundColor: '#fff3cd', border: '1px solid #ffeeba',
  borderRadius: '6px', padding: '0.35rem 0.65rem', marginBottom: '0.5rem',
  fontSize: '0.85rem', fontWeight: 400,
}

export const MultipleChoiceQuestion = ({ question, renderTabs = true }) => {
  const [state, dispatch] = useStateValue()
  const { coursenum, activitySubmissionId, savingGuids, readonly } = state
  const isSaving = savingGuids.has(question.guid)

  useEffect(() => {
    const selected = question.answer ? question.answer.split(',').filter(Boolean).length : 0
    const { minSelections, maxSelections } = question.data
    dispatch({
      type: 'SET_FULLY_ANSWERED', guid: question.guid,
      value: selected >= minSelections && selected <= maxSelections
    })
  }, [question.answer])

  const handleChange = async newAnswer => {
    if (readonly || isSaving) return

    dispatch({ type: 'SET_ANSWER', guid: question.guid, answer: newAnswer })
    dispatch({ type: 'SAVE_START', guid: question.guid })

    try {
      const response = await saveAnswer(coursenum, activitySubmissionId, question.guid, newAnswer)
      dispatch({ type: 'SAVE_SUCCESS', guid: question.guid, payload: response.data })
    } catch (e) {
      dispatch({ type: 'SAVE_ERROR', guid: question.guid })
    }
  }

  const { showFeedback } = state
  const { showAnswer } = state.feedbackSettings
  const showOptionFeedback = showFeedback && showAnswer

  const correctSet = useMemo(() => {
    if (!showOptionFeedback || !question.correctAnswer) return null
    return new Set(question.correctAnswer)
  }, [showOptionFeedback, question.correctAnswer])

  const selectedSet = useMemo(() => {
    const { viewtype } = question.data
    const { answer } = question
    if (!answer) return new Set()
    if (viewtype === 'checkbox') {
      return new Set(answer.split(',').filter(Boolean))
    }
    return new Set([answer])
  }, [question.answer, question.data.viewtype])

  const { viewtype, maxanswers } = question.data
  const isCheckbox = viewtype === 'checkbox'
  const atMax = isCheckbox && maxanswers && selectedSet.size >= maxanswers

  return (
    <>
      <FeedbackDisplay question={question} />
      <div className='position-relative'>
        <SaveIndicator questionGuid={question.guid} />
      </div>
      <TabSplitLayout question={question} renderTabs={renderTabs}>
      <div className="d-flex flex-column align-items-start" style={{ width: '100%' }}>
        <div
          role="heading"
          aria-level={3}
          className="mb-2 text-left"
          dangerouslySetInnerHTML={{ __html: question.text }}
        />

        {correctSet && (
          <div style={FEEDBACK_KEY_STYLE}>
            <span>Key:</span>
            <span style={FEEDBACK_KEY_ITEM_STYLE}>
              <span style={{ ...FEEDBACK_KEY_SWATCH, borderColor: FEEDBACK_COLORS.correct.border, backgroundColor: FEEDBACK_COLORS.correct.bg }} />
              Your correct answer
            </span>
            <span style={FEEDBACK_KEY_ITEM_STYLE}>
              <span style={{ ...FEEDBACK_KEY_SWATCH, borderColor: FEEDBACK_COLORS.incorrect.border, backgroundColor: FEEDBACK_COLORS.incorrect.bg }} />
              Your wrong answer
            </span>
            <span style={FEEDBACK_KEY_ITEM_STYLE}>
              <span style={{ ...FEEDBACK_KEY_SWATCH, borderColor: FEEDBACK_COLORS.missed.border, backgroundColor: FEEDBACK_COLORS.missed.bg }} />
              Correct (missed)
            </span>
          </div>
        )}

        <div id={question.guid} style={{ width: '100%' }}>
          {question.data.options.map(option => (
            <AnswerOption
              key={option.guid}
              option={option}
              question={question}
              onChange={handleChange}
              readonly={readonly}
              correctSet={correctSet}
              selectedSet={selectedSet}
              atMax={atMax}
            />
          ))}
        </div>

        {!readonly && (
          atMax
            ? <div style={MAX_REACHED_STYLE}>Maximum selections reached ({maxanswers}). Deselect an option to choose a different one.</div>
            : <div style={{ ...MAX_REACHED_STYLE, visibility: 'hidden' }}>&nbsp;</div>
        )}
      </div>
      </TabSplitLayout>
    </>
  )
}

function AnswerOption({ option, question, onChange, readonly, correctSet, selectedSet, atMax }) {
  const { viewtype } = question.data
  const { answer } = question
  const isRadio = viewtype === 'radio' || viewtype === 'slider' || viewtype === null
  const isChecked = selectedSet.has(option.guid)
  const isDisabled = readonly || (!isChecked && atMax)

  let optionStyle = OPTION_BASE
  let badge = null
  if (correctSet) {
    const isCorrectOpt = correctSet.has(option.guid)
    const status = getOptionFeedbackStatus(isChecked, isCorrectOpt)
    optionStyle = { ...OPTION_BASE, ...OPTION_FEEDBACK_STYLE[status] }
    if (status !== 'neutral') {
      badge = <span style={FEEDBACK_BADGE[status]}>{FEEDBACK_LABELS[status]}</span>
    }
  }

  if (!isChecked && atMax && !correctSet) {
    optionStyle = { ...optionStyle, opacity: 0.55 }
  }

  const handleRadioChange = () => onChange(option.guid)

  const handleCheckboxChange = e => {
    const selectedGuids = answer ? answer.split(',').filter(Boolean) : []
    let updated = [...selectedGuids]
    if (e.target.checked) {
      updated.push(option.guid)
    } else {
      updated = updated.filter(g => g !== option.guid)
    }
    onChange(updated.join(','))
  }

  return (
    <div style={optionStyle}>
      <label style={isDisabled ? LABEL_DISABLED : LABEL_BASE}>
        <input
          type={isRadio ? 'radio' : 'checkbox'}
          name={isRadio ? `answer-${question.guid}` : undefined}
          value={option.guid}
          checked={isChecked}
          disabled={isDisabled}
          onChange={isRadio ? handleRadioChange : handleCheckboxChange}
          style={INPUT_STYLE}
        />
        <span className="flex-grow-1" dangerouslySetInnerHTML={{ __html: option.text }} />
      </label>
      {badge}
    </div>
  )
}
